import { Box, Paper, Popper } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';

import { RENTAL_PRODUCTS_PATH } from '@/helpers/constants';

import AutocompleteRow from './AutocompleteRow';
import AutocompleteSection from './AutocompleteSection';
import CategoryRail from './CategoryRail';
import DropdownEmptyState from './DropdownEmptyState';
import FreeTextRow from './FreeTextRow';

export default function AutocompleteDropdown({
  open,
  anchorEl,
  listboxId,
  loading,
  productHits,
  categoryRows,
  highlightedIndex,
  productHeading,
  categoryHeading,
  emptyProducts,
  emptyState,
  query,
  variant,
  showFreeTextRow,
  onSelectProduct,
  onSelectCategory,
  onRequestFreeText,
  onOpenContact,
  onHover,
}) {
  const isInline = variant === 'inline';

  const panel = (
    <Paper
      elevation={isInline ? 0 : 4}
      role={emptyState ? undefined : 'listbox'}
      id={listboxId}
      sx={{
        mt: isInline ? 0 : 1,
        borderRadius: isInline
          ? 0
          : theme => `${theme.shape.borderRadiusLarge}px`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...(isInline ? { flex: 1, minHeight: 0 } : { maxHeight: 520 }),
      }}>
      {emptyState ? (
        <DropdownEmptyState
          query={query}
          variant={emptyState}
          onRequestFreeText={onRequestFreeText}
          onOpenContact={onOpenContact}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: isInline ? 'column' : 'row',
            alignItems: 'stretch',
            flex: 1,
            minHeight: 0,
          }}>
          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              minHeight: 0,
              overflowY: 'auto',
            }}>
            <AutocompleteSection
              heading={productHeading}
              loading={loading}
              empty={
                !loading && productHits.length === 0 ? emptyProducts : null
              }>
              {productHits.map((hit, i) => (
                <AutocompleteRow
                  key={`p-${hit.objectID || hit.salesforceId || i}`}
                  id={`${listboxId}-product-${i}`}
                  primary={hit.name}
                  hit={hit}
                  secondary={hit.category_name || hit.productGroupName}
                  highlighted={highlightedIndex === i}
                  href={hit.slug ? `${RENTAL_PRODUCTS_PATH}/${hit.slug}` : null}
                  onSelect={() => onSelectProduct(hit)}
                  onMouseEnter={() => onHover(i)}
                />
              ))}
            </AutocompleteSection>
          </Box>

          <CategoryRail
            categories={categoryRows}
            heading={categoryHeading}
            variant={isInline ? 'pills' : 'rail'}
            idPrefix={listboxId}
            highlightedIndex={highlightedIndex - productHits.length}
            onSelect={onSelectCategory}
            onMouseEnter={i => onHover(productHits.length + i)}
            sx={{ order: -1, flexShrink: 0 }}
          />
        </Box>
      )}

      {/* The empty panel's primary button already offers this, so don't double it up. */}
      {showFreeTextRow && !emptyState && (
        <FreeTextRow
          id={`${listboxId}-freetext`}
          query={query}
          highlighted={
            highlightedIndex === productHits.length + categoryRows.length
          }
          onSelect={onRequestFreeText}
          onMouseEnter={() => onHover(productHits.length + categoryRows.length)}
        />
      )}
    </Paper>
  );

  if (isInline) return panel;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      modifiers={[{ name: 'flip', enabled: false }]}
      style={{ width: anchorEl?.clientWidth, zIndex: 1300 }}>
      {panel}
    </Popper>
  );
}

AutocompleteDropdown.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.any,
  listboxId: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  productHits: PropTypes.array.isRequired,
  categoryRows: PropTypes.array.isRequired,
  highlightedIndex: PropTypes.number,
  productHeading: PropTypes.string.isRequired,
  categoryHeading: PropTypes.string.isRequired,
  emptyProducts: PropTypes.string,
  emptyState: PropTypes.oneOf([null, 'zero_results', 'error']),
  query: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['popper', 'inline']),
  showFreeTextRow: PropTypes.bool,
  onSelectProduct: PropTypes.func.isRequired,
  onSelectCategory: PropTypes.func.isRequired,
  onRequestFreeText: PropTypes.func.isRequired,
  onOpenContact: PropTypes.func.isRequired,
  onHover: PropTypes.func.isRequired,
};

AutocompleteDropdown.defaultProps = {
  emptyState: null,
  highlightedIndex: -1,
  variant: 'popper',
  showFreeTextRow: true,
};
